import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  createBillingRecord,
  fetchPlatformTenant,
  generateInvoiceDraft,
  updateBillingRecord,
} from "../../lib/opsGraphql";

export default function TenantBillingList() {
  const { tenantId } = useParams();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [newRecord, setNewRecord] = useState({
    record_type: "invoice",
    period: new Date().toISOString().slice(0, 7),
    amount_ars: "",
    status: "sent",
    due_date: "",
    notes: "",
  });

  const loadRecords = async (id) => {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const detail = await fetchPlatformTenant(id);
      setRecords(detail?.billing_records || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId) loadRecords(tenantId);
  }, [tenantId]);

  const onCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setInfo("");
    try {
      await createBillingRecord({
        tenant_id: tenantId,
        record_type: newRecord.record_type,
        period: newRecord.period,
        amount_ars: Number(newRecord.amount_ars),
        status: newRecord.status,
        due_date: newRecord.due_date || null,
        notes: newRecord.notes,
      });
      setInfo("Registro creado.");
      await loadRecords(tenantId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const markPaid = async (recordId) => {
    setLoading(true);
    setError("");
    try {
      await updateBillingRecord({
        tenant_id: tenantId,
        record_id: recordId,
        mark_paid: true,
      });
      setInfo("Marcado como pagado.");
      await loadRecords(tenantId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <p>
        <Link to={`/tenants/${tenantId}`}>← Volver a ficha</Link>
      </p>
      <div className="ops-card">
        <h2>Facturación — {tenantId}</h2>
        {error && <p className="ops-error">{error}</p>}
        {info && <p className="ops-info">{info}</p>}
        <button
          type="button"
          className="ops-btn secondary"
          disabled={loading}
          onClick={async () => {
            setLoading(true);
            try {
              await generateInvoiceDraft(tenantId, newRecord.period);
              setInfo("Borrador generado desde plan + consumo.");
              await loadRecords(tenantId);
            } catch (err) {
              setError(err.message);
            } finally {
              setLoading(false);
            }
          }}
        >
          Generar borrador (plan + consumo)
        </button>

        <form className="ops-form-grid" onSubmit={onCreate}>
          <label>
            Tipo
            <select
              value={newRecord.record_type}
              onChange={(e) => setNewRecord({ ...newRecord, record_type: e.target.value })}
            >
              <option value="invoice">Factura</option>
              <option value="payment">Pago</option>
            </select>
          </label>
          <label>
            Período (YYYY-MM)
            <input
              value={newRecord.period}
              onChange={(e) => setNewRecord({ ...newRecord, period: e.target.value })}
            />
          </label>
          <label>
            Monto ARS
            <input
              type="number"
              value={newRecord.amount_ars}
              onChange={(e) => setNewRecord({ ...newRecord, amount_ars: e.target.value })}
              required
            />
          </label>
          <label>
            Estado
            <select
              value={newRecord.status}
              onChange={(e) => setNewRecord({ ...newRecord, status: e.target.value })}
            >
              <option value="draft">draft</option>
              <option value="sent">sent</option>
              <option value="paid">paid</option>
              <option value="overdue">overdue</option>
            </select>
          </label>
          <label>
            Vencimiento
            <input
              type="date"
              value={newRecord.due_date}
              onChange={(e) => setNewRecord({ ...newRecord, due_date: e.target.value })}
            />
          </label>
          <label style={{ gridColumn: "1 / -1" }}>
            Notas
            <input
              value={newRecord.notes}
              onChange={(e) => setNewRecord({ ...newRecord, notes: e.target.value })}
            />
          </label>
          <div>
            <button type="submit" className="ops-btn" disabled={loading || !tenantId}>
              Agregar registro
            </button>
          </div>
        </form>
      </div>

      <div className="ops-card">
        <h2>Registros ({records.length})</h2>
        <table className="ops-table">
          <thead>
            <tr>
              <th>Período</th>
              <th>Tipo</th>
              <th>Monto</th>
              <th>Estado</th>
              <th>Vencimiento</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.record_id}>
                <td>{r.period}</td>
                <td>{r.record_type}</td>
                <td>${(r.amount_ars || 0).toLocaleString("es-AR")}</td>
                <td>
                  <span className={`ops-badge ${r.status === "overdue" ? "danger" : ""}`}>{r.status}</span>
                  {r.line_items?.length > 0 && (
                    <ul className="ops-line-items">
                      {r.line_items.map((li) => (
                        <li key={li.code}>
                          {li.description}: ${(li.amount_ars || 0).toLocaleString("es-AR")}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td>{r.due_date || "—"}</td>
                <td>
                  {r.status !== "paid" && r.record_type === "invoice" && (
                    <button type="button" className="ops-btn secondary" disabled={loading} onClick={() => markPaid(r.record_id)}>
                      Marcar pagado
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
